import React, { memo, useEffect, useLayoutEffect, useRef } from 'react';
import { CheckIcon, PlusIcon } from '@radix-ui/react-icons';
import { cn } from '../../utils/cn';
import { hueVars, isNoStroke, isPlainFill } from './palette';
import { newChecklistItem } from './useMindMapEditor';

const MAX_TEXT = 5000;
const MAX_ITEM = 300;
const MAX_ITEMS = 100;

const Hint = ({ children }) => (
  <p className="mm-shape-hint">
    <span className="mm-hint-mouse">Double-click</span>
    <span className="mm-hint-touch">Double-tap</span> {children}
  </p>
);

const Checklist = ({ shape, editing, onChange, onClose }) => {
  const inputs = useRef(new Map());
  const pendingFocus = useRef(null);
  const items = editing ? shape.items : shape.items.filter((item) => item.text.trim());

  useLayoutEffect(() => {
    if (!pendingFocus.current) return;
    const { id, caret } = pendingFocus.current;
    const input = inputs.current.get(id);
    if (!input) return;
    pendingFocus.current = null;
    input.focus({ preventScroll: true });
    const position = caret === 'start' ? 0 : input.value.length;
    input.setSelectionRange(position, position);
    input.scrollIntoView({ block: 'nearest' });
  });

  const focusItem = (id, caret = 'end') => {
    pendingFocus.current = { id, caret };
  };

  const focusNow = (id) => {
    const input = inputs.current.get(id);
    input?.focus({ preventScroll: true });
    input?.scrollIntoView({ block: 'nearest' });
  };

  const setItems = (next) => onChange({ items: next });

  const handleKeyDown = (event, index) => {
    const item = shape.items[index];
    const input = event.currentTarget;
    if (event.key === 'Enter') {
      event.preventDefault();
      if (shape.items.length >= MAX_ITEMS) return;
      const atStart = input.selectionStart === 0 && input.selectionEnd === 0 && item.text;
      const created = newChecklistItem();
      const insertAt = atStart ? index : index + 1;
      setItems([...shape.items.slice(0, insertAt), created, ...shape.items.slice(insertAt)]);
      focusItem(atStart ? item.id : created.id, atStart ? 'start' : 'end');
    } else if (event.key === 'Backspace' && !item.text && shape.items.length > 1) {
      event.preventDefault();
      const neighbour = index > 0 ? shape.items[index - 1] : shape.items[index + 1];
      setItems(shape.items.filter((candidate) => candidate.id !== item.id));
      focusItem(neighbour.id);
    } else if (event.key === 'ArrowUp' && index > 0 && input.selectionStart === 0) {
      event.preventDefault();
      focusNow(shape.items[index - 1].id);
    } else if (event.key === 'ArrowDown' && index < shape.items.length - 1 && input.selectionEnd === input.value.length) {
      event.preventDefault();
      focusNow(shape.items[index + 1].id);
    } else if (event.key === 'Escape') {
      onClose();
    }
  };

  if (!editing && items.length === 0) return <Hint>to add to-dos</Hint>;

  const doneCount = items.filter((item) => item.done).length;

  return (
    <>
      <ul className="mm-checklist">
        {items.map((item, index) => (
          <li key={item.id} className={cn('mm-check-item', item.done && 'mm-check-done')}>
            <button
              type="button"
              role="checkbox"
              aria-checked={item.done}
              aria-label={item.text || 'To-do'}
              data-action="toggle-item"
              data-item-id={item.id}
              className="mm-checkbox"
              onClick={
                editing
                  ? () => setItems(shape.items.map((candidate) => (candidate.id === item.id ? { ...candidate, done: !candidate.done } : candidate)))
                  : undefined
              }
            >
              {item.done && <CheckIcon />}
            </button>
            {editing ? (
              <span className="mm-item-edit" data-value={`${item.text}\u200b`}>
                <textarea
                  ref={(element) => (element ? inputs.current.set(item.id, element) : inputs.current.delete(item.id))}
                  data-item-input
                  rows={1}
                  value={item.text}
                  maxLength={MAX_ITEM}
                  placeholder="To-do"
                  aria-label="To-do"
                  onChange={(event) =>
                    setItems(
                      shape.items.map((candidate) =>
                        candidate.id === item.id ? { ...candidate, text: event.target.value.replace(/\n/g, ' ') } : candidate
                      )
                    )
                  }
                  onKeyDown={(event) => handleKeyDown(event, index)}
                />
              </span>
            ) : (
              <span className="mm-check-text">{item.text}</span>
            )}
          </li>
        ))}
      </ul>
      {editing && shape.items.length < MAX_ITEMS && (
        <button
          type="button"
          className="mm-add-item"
          onClick={() => {
            const created = newChecklistItem();
            setItems([...shape.items, created]);
            focusItem(created.id);
          }}
        >
          <PlusIcon />
          Add to-do
        </button>
      )}
      {!editing && items.length > 0 && (
        <span className="mm-check-progress" aria-label={`${doneCount} of ${items.length} done`}>
          {doneCount}/{items.length}
        </span>
      )}
    </>
  );
};

const MindMapShape = memo(function MindMapShape({ shape, selected, editing, onChange, onClose }) {
  const bodyRef = useRef(null);
  const textRef = useRef(null);

  useEffect(() => {
    if (!editing) return;
    const field = textRef.current ?? [...bodyRef.current.querySelectorAll('[data-item-input]')].at(-1);
    if (!field || document.activeElement === field) return;
    field.focus({ preventScroll: true });
    field.setSelectionRange(field.value.length, field.value.length);
  }, [editing]);

  const change = (patch) => onChange(shape.id, patch);

  return (
    <div
      data-shape-id={shape.id}
      className={cn(
        'mm-shape',
        isPlainFill(shape.fill) && 'mm-fill-plain',
        isNoStroke(shape.stroke) && 'mm-stroke-none',
        selected && 'mm-shape-selected',
        editing && 'mm-shape-editing'
      )}
      style={{
        ...hueVars(shape.fill, shape.stroke),
        width: shape.width,
        height: shape.height,
        transform: `translate(${shape.x}px, ${shape.y}px)`,
      }}
    >
      <div ref={bodyRef} className="mm-shape-body">
        {shape.kind === 'CHECKLIST' ? (
          <Checklist shape={shape} editing={editing} onChange={change} onClose={onClose} />
        ) : editing ? (
          <textarea
            ref={textRef}
            value={shape.text}
            maxLength={MAX_TEXT}
            placeholder="Write anything…"
            aria-label="Note text"
            onChange={(event) => change({ text: event.target.value })}
            onKeyDown={(event) => event.key === 'Escape' && onClose()}
          />
        ) : shape.text ? (
          <div className="mm-shape-text">{shape.text}</div>
        ) : (
          <Hint>to write</Hint>
        )}
      </div>
      <div data-action="resize" className="mm-resize" aria-hidden="true" />
      {editing && (
        <button type="button" className="mm-shape-done" onClick={onClose}>
          <CheckIcon />
          Done
        </button>
      )}
    </div>
  );
});

export default MindMapShape;
